import { formatDateValue } from './DateRangePicker.helpers';
import {
  DateRangePickerPresetKey,
  type DateRangePickerPreset,
  type DateRangePickerValue,
} from './DateRangePicker.types';

export const dateRangePickerPresetLabels: Record<DateRangePickerPresetKey, string> = {
  [DateRangePickerPresetKey.Today]: 'Today',
  [DateRangePickerPresetKey.Yesterday]: 'Yesterday',
  [DateRangePickerPresetKey.ThisWeek]: 'This week',
  [DateRangePickerPresetKey.ThisMonth]: 'This month',
  [DateRangePickerPresetKey.LastMonth]: 'Last month',
  [DateRangePickerPresetKey.Last3Months]: 'Last 3 months',
  [DateRangePickerPresetKey.Last6Months]: 'Last 6 months',
  [DateRangePickerPresetKey.ThisYear]: 'This year',
  [DateRangePickerPresetKey.Last7Days]: 'Last 7 days',
  [DateRangePickerPresetKey.Last30Days]: 'Last 30 days',
  [DateRangePickerPresetKey.Last12Months]: 'Last 12 months',
  [DateRangePickerPresetKey.LastYear]: 'Last year',
};

export interface DateRangePickerPresetOptions {
  endPeriodsYesterday?: boolean;
}

export function buildDateRangePickerPresets(
  presetKeys: readonly DateRangePickerPresetKey[],
  options: DateRangePickerPresetOptions = {},
): DateRangePickerPreset[] {
  return presetKeys.map((key) => ({
    key,
    label: dateRangePickerPresetLabels[key],
    getValue: () => getDateRangePickerPresetValue(key, new Date(), options),
  }));
}

export function getDateRangePickerPresetValue(
  preset: DateRangePickerPresetKey,
  now = new Date(),
  options: DateRangePickerPresetOptions = {},
): Pick<DateRangePickerValue, 'dateFrom' | 'dateTo'> {
  const end = options.endPeriodsYesterday ? addDays(now, -1) : now;
  switch (preset) {
    case DateRangePickerPresetKey.Today:
      return rangeFromDates(now, now);
    case DateRangePickerPresetKey.Yesterday: {
      const yesterday = addDays(now, -1);
      return rangeFromDates(yesterday, yesterday);
    }
    case DateRangePickerPresetKey.ThisWeek:
      return rangeFromDates(startOfWeek(now), now);
    case DateRangePickerPresetKey.ThisMonth:
      return rangeFromDates(startOfMonth(now), end);
    case DateRangePickerPresetKey.LastMonth: {
      const lastMonth = addMonths(now, -1);
      return rangeFromDates(startOfMonth(lastMonth), endOfMonth(lastMonth));
    }
    case DateRangePickerPresetKey.Last3Months:
      return previousCompleteMonthRange(now, 3);
    case DateRangePickerPresetKey.Last6Months:
      return previousCompleteMonthRange(now, 6);
    case DateRangePickerPresetKey.ThisYear:
      return rangeFromDates(startOfYear(now), end);
    case DateRangePickerPresetKey.LastYear: {
      const lastYear = new Date(now.getFullYear() - 1, 0, 1);
      return rangeFromDates(startOfYear(lastYear), endOfYear(lastYear));
    }
    case DateRangePickerPresetKey.Last30Days:
      return rangeFromDates(addDays(end, -29), end);
    case DateRangePickerPresetKey.Last12Months:
      return previousCompleteMonthRange(now, 12);
    case DateRangePickerPresetKey.Last7Days:
    default:
      return rangeFromDates(addDays(now, -7), addDays(now, -1));
  }
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number) {
  const targetMonthStart = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const targetMonthEnd = endOfMonth(targetMonthStart);
  const targetDay = Math.min(date.getDate(), targetMonthEnd.getDate());
  return new Date(targetMonthStart.getFullYear(), targetMonthStart.getMonth(), targetDay);
}

function endOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function previousCompleteMonthRange(now: Date, monthsBackwards: number) {
  return rangeFromDates(
    startOfMonth(addMonths(now, -monthsBackwards)),
    endOfMonth(addMonths(now, -1)),
  );
}

function rangeFromDates(from: Date, to: Date) {
  return {
    dateFrom: formatDateValue(from),
    dateTo: formatDateValue(to),
  };
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfWeek(date: Date) {
  const next = new Date(date);
  next.setDate(next.getDate() - next.getDay());
  return next;
}

function startOfYear(date: Date) {
  return new Date(date.getFullYear(), 0, 1);
}

function endOfYear(date: Date) {
  return new Date(date.getFullYear(), 11, 31);
}
